/**
 * Editor store: the in-memory editing state for whichever layout is
 * currently open. This is NOT the persistence layer — saving to Supabase
 * happens via the layouts API (src/api/layouts.ts) and the useAutosave
 * hook, which both read from this store. This store only owns:
 *
 *   - the current object graph being edited
 *   - selection state (single or multi)
 *   - the undo/redo history stack
 *   - validation issues (recomputed after every committed mutation)
 *
 * Performance note: components should subscribe to narrow slices (e.g.
 * "just this one object by id") rather than the whole `objects` array,
 * or every drag will re-render the entire canvas. See
 * components/canvas/shapes/* for the selector pattern this enables.
 */

import { create } from 'zustand';
import type { LayoutObject, ValidationContext, ValidationIssue } from '../domain/types';
import { validateLayout } from '../domain/validation/validateLayout';

interface HistoryEntry {
  objects: LayoutObject[];
}

interface EditorState {
  objects: LayoutObject[];
  selectedIds: string[];
  validationIssues: ValidationIssue[];
  validationContext: ValidationContext | null;
  /** Persisted per-layout: when false, the validation warnings section
   * is suppressed from the printed setup sheet. */
  showWarningsOnSetupSheet: boolean;

  history: HistoryEntry[];
  historyIndex: number;

  // --- Setup ---
  loadLayout: (objects: LayoutObject[], context: ValidationContext, showWarningsOnSetupSheet?: boolean) => void;
  setValidationContext: (context: ValidationContext) => void;
  setShowWarningsOnSetupSheet: (show: boolean) => void;

  // --- Selection ---
  selectObject: (id: string | null) => void;
  selectMultiple: (ids: string[]) => void;
  toggleSelection: (id: string) => void;
  /** Selects every object currently on the canvas. */
  selectAll: () => void;
  /** Selects all chairs that share the same rowGroupId as the given object id.
   * If that object has no rowGroupId, falls back to selecting just that object. */
  selectRowGroup: (id: string) => void;
  clearSelection: () => void;

  // --- Clipboard ---
  clipboard: LayoutObject[];
  copySelected: () => void;
  pasteClipboard: () => void;

  // --- Mutations (each commits to history + re-validates) ---
  addObject: (object: LayoutObject) => void;
  updateObject: (id: string, changes: Partial<LayoutObject>) => void;
  /**
   * Moves a dragged object to (x, y). If that object is part of a
   * multi-selection (selectedIds.length > 1 and includes id), every other
   * selected, unlocked object moves by the same delta, committed as a
   * single atomic history entry — so a group drag undoes/redoes as one
   * step, not N separate ones. If the dragged object is not part of a
   * multi-selection, this behaves identically to updateObject(id, {x,y}).
   */
  moveObjectWithSelection: (id: string, x: number, y: number) => void;
  deleteObject: (id: string) => void;
  deleteSelected: () => void;
  duplicateObject: (id: string) => void;
  duplicateSelected: () => void;
  toggleLock: (id: string) => void;
  /** Locks (or unlocks, if all are already locked) every selected object
   * in a single committed history entry — same reasoning as
   * deleteSelected: a coordinator pressing "Lock all" on a 5-object
   * selection should be able to undo it with one press of Undo, not five. */
  toggleLockSelected: () => void;

  // --- Undo/redo ---
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // --- Derived ---
  runValidation: () => void;
}

const MAX_HISTORY = 50;

function cloneObjects(objects: LayoutObject[]): LayoutObject[] {
  return objects.map((o) => ({ ...o }));
}

export const useEditorStore = create<EditorState>((set, get) => ({
  objects: [],
  selectedIds: [],
  validationIssues: [],
  validationContext: null,
  showWarningsOnSetupSheet: true,
  history: [],
  historyIndex: -1,
  clipboard: [],

  loadLayout: (objects, context, showWarningsOnSetupSheet = true) => {
    const cloned = cloneObjects(objects);
    set({
      objects: cloned,
      selectedIds: [],
      validationContext: context,
      showWarningsOnSetupSheet,
      history: [{ objects: cloneObjects(cloned) }],
      historyIndex: 0,
    });
    get().runValidation();
  },

  setShowWarningsOnSetupSheet: (show) => {
    set({ showWarningsOnSetupSheet: show });
  },

  setValidationContext: (context) => {
    set({ validationContext: context });
    get().runValidation();
  },

  selectObject: (id) => set({ selectedIds: id ? [id] : [] }),
  selectMultiple: (ids) => set({ selectedIds: ids }),
  selectAll: () => set((state) => ({ selectedIds: state.objects.map((o) => o.id) })),

  selectRowGroup: (id) => {
    const { objects } = get();
    const target = objects.find((o) => o.id === id);
    if (!target?.rowGroupId) {
      set({ selectedIds: [id] });
      return;
    }
    const groupIds = objects
      .filter((o) => o.rowGroupId === target.rowGroupId)
      .map((o) => o.id);
    set({ selectedIds: groupIds });
  },

  clearSelection: () => set({ selectedIds: [] }),

  toggleSelection: (id) => {
    set((state) => {
      const isSelected = state.selectedIds.includes(id);
      return {
        selectedIds: isSelected
          ? state.selectedIds.filter((sid) => sid !== id)
          : [...state.selectedIds, id],
      };
    });
  },

  copySelected: () => {
    const { objects, selectedIds } = get();
    const toCopy = objects.filter((o) => selectedIds.includes(o.id));
    if (toCopy.length === 0) return;
    set({ clipboard: cloneObjects(toCopy) });
  },

  pasteClipboard: () => {
    const { clipboard } = get();
    if (clipboard.length === 0) return;

    // Paste with a small offset so pasted copies are visibly distinct
    // from their source rather than stacking exactly on top of it —
    // same convention as duplicateObject below.
    const pasted: LayoutObject[] = clipboard.map((o) => ({
      ...o,
      id: crypto.randomUUID(),
      x: o.x + 1.5,
      y: o.y + 1.5,
      locked: false,
    }));

    commitChange(set, get, (objects) => [...objects, ...pasted]);
    set({ selectedIds: pasted.map((o) => o.id) });
  },

  addObject: (object) => {
    commitChange(set, get, (objects) => [...objects, object]);
  },

  updateObject: (id, changes) => {
    commitChange(set, get, (objects) =>
      objects.map((o) => (o.id === id ? { ...o, ...changes } : o))
    );
  },

  moveObjectWithSelection: (id, x, y) => {
    const { objects, selectedIds } = get();
    const draggedObject = objects.find((o) => o.id === id);
    if (!draggedObject) return;

    const isPartOfMultiSelection = selectedIds.length > 1 && selectedIds.includes(id);

    if (!isPartOfMultiSelection) {
      commitChange(set, get, (objs) => objs.map((o) => (o.id === id ? { ...o, x, y } : o)));
      return;
    }

    const deltaX = x - draggedObject.x;
    const deltaY = y - draggedObject.y;

    commitChange(set, get, (objs) =>
      objs.map((o) => {
        if (o.id === id) return { ...o, x, y };
        if (selectedIds.includes(o.id) && !o.locked) {
          return { ...o, x: o.x + deltaX, y: o.y + deltaY };
        }
        return o;
      })
    );
  },

  deleteObject: (id) => {
    commitChange(set, get, (objects) => objects.filter((o) => o.id !== id));
    set((state) => ({ selectedIds: state.selectedIds.filter((sid) => sid !== id) }));
  },

  deleteSelected: () => {
    const { selectedIds } = get();
    commitChange(set, get, (objects) => objects.filter((o) => !selectedIds.includes(o.id)));
    set({ selectedIds: [] });
  },

  duplicateObject: (id) => {
    const source = get().objects.find((o) => o.id === id);
    if (!source) return;
    const duplicate: LayoutObject = {
      ...source,
      id: crypto.randomUUID(),
      x: source.x + 1,
      y: source.y + 1,
      label: `${source.label} (copy)`,
      locked: false,
    };
    commitChange(set, get, (objects) => [...objects, duplicate]);
    set({ selectedIds: [duplicate.id] });
  },

  duplicateSelected: () => {
    const { objects, selectedIds } = get();
    if (selectedIds.length === 0) return;
    const sources = objects.filter((o) => selectedIds.includes(o.id));
    const duplicates: LayoutObject[] = sources.map((source) => ({
      ...source,
      id: crypto.randomUUID(),
      x: source.x + 1,
      y: source.y + 1,
      label: `${source.label} (copy)`,
      locked: false,
    }));
    commitChange(set, get, (objs) => [...objs, ...duplicates]);
    set({ selectedIds: duplicates.map((o) => o.id) });
  },

  toggleLock: (id) => {
    commitChange(set, get, (objects) =>
      objects.map((o) => (o.id === id ? { ...o, locked: !o.locked } : o))
    );
  },

  toggleLockSelected: () => {
    const { objects, selectedIds } = get();
    if (selectedIds.length === 0) return;
    const selectedObjects = objects.filter((o) => selectedIds.includes(o.id));
    const allLocked = selectedObjects.every((o) => o.locked);
    const nextLockedState = !allLocked;

    commitChange(set, get, (objs) =>
      objs.map((o) => (selectedIds.includes(o.id) ? { ...o, locked: nextLockedState } : o))
    );
  },

  undo: () => {
    const { history, historyIndex } = get();
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    set({
      objects: cloneObjects(history[newIndex].objects),
      historyIndex: newIndex,
    });
    get().runValidation();
  },

  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    set({
      objects: cloneObjects(history[newIndex].objects),
      historyIndex: newIndex,
    });
    get().runValidation();
  },

  canUndo: () => get().historyIndex > 0,
  canRedo: () => get().historyIndex < get().history.length - 1,

  runValidation: () => {
    const { objects, validationContext } = get();
    if (!validationContext) {
      set({ validationIssues: [] });
      return;
    }
    const issues = validateLayout(objects, validationContext);
    set({ validationIssues: issues });
  },
}));

/**
 * Shared helper for every mutation: applies the change, pushes a new
 * history entry (truncating any "future" redo states, standard undo
 * stack behavior), caps history length, and re-runs validation.
 *
 * This is the ONE place a mutation gets committed — every store action
 * above routes through this, so undo/redo and validation can never get
 * out of sync with what's actually rendered (PRD §9.5, §16.2).
 */
function commitChange(
  set: (partial: Partial<EditorState> | ((state: EditorState) => Partial<EditorState>)) => void,
  get: () => EditorState,
  updater: (objects: LayoutObject[]) => LayoutObject[]
) {
  const { objects, history, historyIndex } = get();
  const newObjects = updater(objects);

  const truncatedHistory = history.slice(0, historyIndex + 1);
  const newHistory = [...truncatedHistory, { objects: cloneObjects(newObjects) }].slice(-MAX_HISTORY);

  set({
    objects: newObjects,
    history: newHistory,
    historyIndex: newHistory.length - 1,
  });

  get().runValidation();
}
